import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import ExportButton from '@/components/ExportButton';
import { CruscottoSezione } from '@/pages/Cruscotto';
import ArchiveCondominiumDialog from '@/components/ArchiveCondominiumDialog';
import { toast } from 'sonner';
import { Building2, Users, AlertCircle, TrendingUp, TrendingDown, Euro, Clock, FileText, Plus, AlertTriangle, Bell, Wrench, ChevronRight, RefreshCw, FileWarning, Truck, HelpCircle, Mail, Archive, MapPin, CheckCircle2, BookOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Alert type icons and colors
const ALERT_CONFIG = {
  DELINQUENCY: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  PAYMENT_DUE: { icon: Euro, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  F24_DUE: { icon: FileWarning, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  COMMUNICATION_URGENT: { icon: Bell, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' },
  INCIDENT_REPORT: { icon: Wrench, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
  CONTRACT_EXPIRY: { icon: Truck, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' }
};

// Anni selezionabili in Dashboard: dal corrente indietro di otto
const ANNI_DASHBOARD = Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - i);

const PRIORITY_COLORS = {
CRITICAL: 'bg-red-500',
  HIGH: 'bg-orange-500',
  MEDIUM: 'bg-yellow-500',
  LOW: 'bg-blue-500'
};

// Simple bar chart component
const SimpleBarChart = ({ data, color, label }) => {
  const maxValue = Math.max(...data.map(d => d.amount), 1);
  const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <div className="flex items-end gap-1 h-24">
        {data.map((item, i) => {
          // Altezza in pixel: una percentuale dentro una colonna flex ad altezza
          // automatica si risolve a zero, e le barre non si vedevano pur avendo i dati.
          const px = maxValue > 0 ? Math.round((item.amount / maxValue) * 72) : 0;
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1 h-full">
              <div
                className={`w-full rounded-t ${color}`}
                style={{ height: `${Math.max(px, 2)}px` }}
title={`${months[i]}: €${item.amount.toLocaleString('it-IT')}`}
              />
              <span className="text-xs text-muted-foreground">{months[i].slice(0, 1)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Alert Card Component
const AlertCard = ({ alert, onClick }) => {
  const config = ALERT_CONFIG[alert.type] || ALERT_CONFIG.COMMUNICATION_URGENT;
  const Icon = config.icon;
  
  return (
    <div 
      className={`p-3 rounded-lg border ${config.bg} ${config.border} cursor-pointer hover:shadow-md transition-all`}
      onClick={() => onClick && onClick(alert)}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-full ${config.bg}`}>
          <Icon className={`h-4 w-4 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-2 h-2 rounded-full ${PRIORITY_COLORS[alert.priority]}`} />
            <h4 className="text-sm font-medium text-slate-900 truncate">{alert.title}</h4>
          </div>
          <p className="text-xs text-slate-600 line-clamp-2">{alert.description}</p>
          {alert.amount && (
            <p className="text-sm font-semibold text-slate-900 mt-1">
              €{alert.amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
            </p>
          )}
          {alert.due_date && (
            <p className="text-xs text-slate-500 mt-1">
              <Clock className="inline h-3 w-3 mr-1" />
              Scadenza: {new Date(alert.due_date).toLocaleDateString('it-IT')}
            </p>
          )}
        </div>
        <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
      </div>
    </div>
  );
};

export const Dashboard = () => {
  const { token, selectedCondominium, condominiums, selectCondominium, refreshCondominiums, user } = useAuth();
  const navigate = useNavigate();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;
  const condoName = selectedCondominium?.condominium?.name || 'Condominio';
  
  const [stats, setStats] = useState(null);
  // Esercizio mostrato: la rotta accetta ?year=, la pagina non lo mandava mai.
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [alerts, setAlerts] = useState([]);
const [alertsSummary, setAlertsSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(true);
  
  // New Condominium Dialog
  const [newCondoOpen, setNewCondoOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // Archive Condominium Dialog
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [newCondoForm, setNewCondoForm] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    vat_number: '',
    fiscal_code: '',
    pec: '',
    iban: ''
  });

  const fetchDashboard = useCallback(async () => {
    if (!condoId) { setLoading(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/dashboard?year=${anno}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
    setLoading(false);
  }, [condoId, token, anno]);

  const fetchAlerts = useCallback(async () => {
    if (!condoId) { setAlertsLoading(false); return; }
    setAlertsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
        setAlertsSummary(data.summary || null);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
    setAlertsLoading(false);
  }, [condoId, token]);

  useEffect(() => {
    fetchDashboard();
    fetchAlerts();
  }, [fetchDashboard, fetchAlerts]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const isAdmin = userRole === 'ADMIN';
  const isAccountant = userRole === 'ACCOUNTANT';
  const canSeeDetails = isAdmin || isAccountant;

  const handleAlertClick = (alert) => {
    // Navigate to relevant page based on alert type
    switch (alert.related_type) {
      case 'unit':
        navigate('/registry');
        break;
      case 'expense':
        navigate('/expenses');
        break;
      case 'f24':
        navigate('/tax');
        break;
      case 'supplier':
        navigate('/suppliers');
        break;
      case 'communication':
        navigate('/communications');
        break;
      case 'incident':
        navigate('/incidents');
        break;
      default:
        break;
    }
  };

  const handleCreateCondominium = async () => {
    if (!newCondoForm.name.trim()) {
      toast.error('Inserisci il nome del condominio');
      return;
    }
    
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newCondoForm.name,
          address: newCondoForm.address || null,
          city: newCondoForm.city || null,
          postal_code: newCondoForm.postal_code || null,
          vat_number: newCondoForm.vat_number || null
        })
      });
      
      if (res.ok) {
        toast.success('Condominio creato con successo!');
        setNewCondoOpen(false);
        setNewCondoForm({ name: '', address: '', city: '', postal_code: '', vat_number: '', fiscal_code: '', pec: '', iban: '' });
        // Refresh condominiums list
        if (refreshCondominiums) {
          await refreshCondominiums();
        }
      } else {
        const error = await res.json();
        if (res.status === 402) {
          toast.error('Limite piano raggiunto. Effettua upgrade per aggiungere altri condomini.');
        } else {
          toast.error(error.detail || 'Errore nella creazione');
        }
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setCreating(false);
    }
  };

  // Count alerts by priority
  const criticalCount = alertsSummary?.critical || 0;
  const highCount = alertsSummary?.high || 0;

  // Nessun condominio ancora: form di creazione inline (percorso robusto).
  // Usa direttamente handleCreateCondominium -> POST /api/condominiums -> refreshCondominiums
  // (AuthContext auto-seleziona il primo condominio => ruolo ADMIN => menu completo).
  // NB: non si rimanda a /onboarding perché quel flusso non aggiornava il contesto
  // (restava "Nessun ruolo" e menu nascosto fino a un reload manuale).
  if (!condominiums || condominiums.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[70vh] p-6" data-testid="dashboard-empty">
        <div className="max-w-md w-full space-y-6 bg-white border border-stone-200 rounded-lg p-8 shadow-sm">
          <div className="text-center space-y-3">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-md bg-slate-900 mx-auto">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              Benvenuto su Artego Condo Manager
            </h1>
            <p className="text-stone-600">
              Per iniziare, crea il tuo primo condominio: diventerai automaticamente amministratore.
            </p>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="empty-name">Nome Condominio *</Label>
              <Input
                id="empty-name"
                value={newCondoForm.name}
                onChange={(e) => setNewCondoForm({ ...newCondoForm, name: e.target.value })}
                placeholder="Es. Condominio Via Roma 1"
                data-testid="empty-condo-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="empty-address">Indirizzo</Label>
                <Input
                  id="empty-address"
                  value={newCondoForm.address}
                  onChange={(e) => setNewCondoForm({ ...newCondoForm, address: e.target.value })}
                  placeholder="Via/Piazza..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="empty-city">Città</Label>
                <Input
                  id="empty-city"
                  value={newCondoForm.city}
                  onChange={(e) => setNewCondoForm({ ...newCondoForm, city: e.target.value })}
                  placeholder="Palermo"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="empty-cap">CAP</Label>
                <Input
                  id="empty-cap"
                  value={newCondoForm.postal_code}
                  onChange={(e) => setNewCondoForm({ ...newCondoForm, postal_code: e.target.value })}
                  placeholder="90100"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="empty-vat">Codice Fiscale / P.IVA</Label>
                <Input
                  id="empty-vat"
                  value={newCondoForm.vat_number}
                  onChange={(e) => setNewCondoForm({ ...newCondoForm, vat_number: e.target.value })}
                  placeholder="Opzionale"
                />
              </div>
            </div>
            <Button
              onClick={handleCreateCondominium}
              disabled={creating}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 py-3"
              data-testid="empty-create-condo"
            >
              {creating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Crea il tuo primo condominio
            </Button>
            <p className="text-center text-xs text-stone-500">
              Potrai poi aggiungere unità, proprietari e tabelle millesimali dall'Anagrafica.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Support Header Bar */}
      <div className="flex items-center justify-end gap-2 -mt-2 mb-2">
        <Button
          size="sm"
          onClick={() => navigate('/guida')}
          data-testid="dashboard-guida-btn"
        >
          <BookOpen className="h-4 w-4 mr-2" />
          Guida
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/faq')}
          className="text-stone-600 hover:text-slate-900"
        >
          <HelpCircle className="h-4 w-4 mr-2" />
          FAQ
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.location.href = 'mailto:support.app@artegosrls.it'}
          className="text-stone-600 hover:text-slate-900"
        >
          <Mail className="h-4 w-4 mr-2" />
          Supporto
        </Button>
      </div>

      {/* Header con grafica brand */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary to-[#072628] px-5 py-6 sm:px-7 sm:py-8 shadow-sm">
        <img
          src="/assets/artego-symbol.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute -right-8 -bottom-12 h-48 w-48 opacity-10"
        />
        <div className="relative flex items-center gap-4">
          <img
            src="/assets/artego-symbol.png"
            alt="Artego"
            className="hidden sm:block h-14 w-14 rounded-full ring-2 ring-white/20 flex-shrink-0"
          />
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">
              Dashboard
            </h1>
            <p className="text-sm sm:text-base text-white/80 mt-1">
              {condoName} • Esercizio {stats?.period || new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>

      {/* Azioni */}
      <div className="flex flex-wrap gap-2 justify-end">
        {isAdmin && (
          <>
            <Button
              onClick={() => setNewCondoOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Nuovo Condominio</span>
              <span className="sm:hidden">Nuovo</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setArchiveDialogOpen(true)}
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
            >
              <Archive className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Archivia</span>
            </Button>
          </>
        )}
        <select
          value={anno}
          onChange={(e) => setAnno(Number(e.target.value))}
          className="h-9 rounded-md border border-stone-300 bg-white px-2 text-sm text-stone-700"
          title="Esercizio mostrato dalle card e dai grafici"
          data-testid="dashboard-anno"
        >
          {ANNI_DASHBOARD.map((a) => (
            <option key={a} value={a}>Esercizio {a}</option>
          ))}
        </select>
        <ExportButton
          condominiumId={condoId}
          exportType="dashboard-summary"
title="Esporta"
          formats={['print', 'pdf']}
          showArchive={true}
          filters={{ year: stats?.period || new Date().getFullYear() }}
          token={token}
          variant="outline"
          size="sm"
        />
      </div>

      {/* Condominium Cards Section - For Admin users */}
      {isAdmin && condominiums && condominiums.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              I Miei Condomini ({condominiums.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {condominiums.map((item) => {
              const condo = item.condominium;
              const role = item.role;
              const isSelected = selectedCondominium?.condominium?.id === condo.id;
              const isArchived = condo.status === 'ARCHIVED';
              
              return (
                <Card 
                  key={condo.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    isSelected 
                      ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50/50' 
                      : isArchived 
                        ? 'border-gray-300 bg-gray-50 opacity-70' 
                        : 'border-stone-200 hover:border-blue-300'
                  }`}
                  onClick={() => {
                    if (!isArchived) {
                      selectCondominium(item);
                      toast.success(`Selezionato: ${condo.name}`);
                    }
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {isSelected && (
                            <CheckCircle2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          )}
                          <h3 className={`font-semibold text-sm truncate ${isArchived ? 'text-gray-500' : 'text-slate-800'}`}>
                            {condo.name}
                          </h3>
                        </div>
                        <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          <span className="truncate">{condo.address}, {condo.city}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            role === 'ADMIN' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                            role === 'ACCOUNTANT' ? 'bg-green-100 text-green-700 border-green-300' :
                            role === 'RESIDENT' ? 'bg-purple-100 text-purple-700 border-purple-300' :
                            'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {role === 'ADMIN' ? 'Admin' : 
                           role === 'ACCOUNTANT' ? 'Contabile' : 
                           role === 'RESIDENT' ? 'Residente' : role}
                        </Badge>
                        {isArchived && (
                          <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600">
                            <Archive className="h-3 w-3 mr-1" />
                            Archiviato
                          </Badge>
                        )}
                      </div>
                    </div>
                    {/* Quick Stats */}
                    {!isArchived && (
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          <span>{condo.unit_count || '-'} unità</span>
                        </div>
                        {condo.vat_number && (
                          <div className="flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            <span className="truncate">{condo.vat_number}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Alerts Section - Priority Display */}
      {canSeeDetails && (criticalCount > 0 || highCount > 0) && (
        <Card className="border-red-200 bg-red-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <CardTitle className="text-lg font-semibold text-red-900">
                  Attenzione Richiesta
                </CardTitle>
              </div>
              <div className="flex gap-2">
                {criticalCount > 0 && (
                  <Badge className="bg-red-500 text-white">{criticalCount} Critiche</Badge>
                )}
                {highCount > 0 && (
                  <Badge className="bg-orange-500 text-white">{highCount} Urgenti</Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="max-h-64">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {alerts.filter(a => a.priority === 'CRITICAL' || a.priority === 'HIGH').slice(0, 6).map((alert) => (
                  <AlertCard key={alert.id} alert={alert} onClick={handleAlertClick} />
                ))}
              </div>
            </ScrollArea>
            {alertsSummary?.total > 6 && (
              <Button variant="link" className="mt-2 p-0 h-auto text-red-700" onClick={() => navigate('/reports')}>
                Vedi tutte le {alertsSummary.total} segnalazioni →
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
      ) : !stats ? (
        <div className="text-center py-8 text-muted-foreground">
          Nessun dato disponibile
        </div>
      ) : (
        <>
          {/* Main Stats Grid - Responsive */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <Card className="border-stone-200 shadow-sm hover:shadow-md transition-all">
              <CardHeader className="flex flex-row items-center justify-between pb-2 px-3 sm:px-6">
                <CardTitle className="text-xs sm:text-sm font-medium text-stone-600">
                  Unità
                </CardTitle>
                <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-stone-400" strokeWidth={1.5} />
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                <div className="text-xl sm:text-3xl font-heading font-bold text-slate-900">
                  {stats.total_units}
                </div>
                <p className="text-xs text-stone-600 mt-1 hidden sm:block">
                  {stats.total_residents} soggetti in anagrafica
</p>
              </CardContent>
            </Card>

            <Card className="border-stone-200 shadow-sm hover:shadow-md transition-all">
              <CardHeader className="flex flex-row items-center justify-between pb-2 px-3 sm:px-6">
                <CardTitle className="text-xs sm:text-sm font-medium text-stone-600">
                  Entrate
                </CardTitle>
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" strokeWidth={1.5} />
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                <div className="text-lg sm:text-3xl font-heading font-bold text-green-600">
                  {formatCurrency(stats.quotes_collected ?? stats.total_income)}
                </div>
                {/* Prima mostrava gli accrediti bancari con l'etichetta «Quote e
                    versamenti»: ora sono le quote incassate dell'esercizio, e la
                    banca è dichiarata a parte. */}
                <p className="text-xs text-stone-600 mt-1 hidden sm:block">
                  {stats.quotes_collected != null
                    ? `Quote incassate su ${formatCurrency(stats.quotes_issued)} emesse`
                    : 'Quote e versamenti'}
                </p>
                {stats.bank_income != null && (
                  <p className="text-xs text-stone-500 mt-0.5 hidden sm:block">
                    In banca: {formatCurrency(stats.bank_income)} di accrediti
                  </p>
                )}
</CardContent>
            </Card>

            <Card className="border-stone-200 shadow-sm hover:shadow-md transition-all">
              <CardHeader className="flex flex-row items-center justify-between pb-2 px-3 sm:px-6">
                <CardTitle className="text-xs sm:text-sm font-medium text-stone-600">
                  Uscite
                </CardTitle>
                <TrendingDown className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" strokeWidth={1.5} />
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                <div className="text-lg sm:text-3xl font-heading font-bold text-red-600">
                  {formatCurrency(stats.total_expenses)}
                </div>
                <p className="text-xs text-stone-600 mt-1 hidden sm:block">
                  {stats.expenses_source === 'banca'
                    ? 'Addebiti in banca (nessuna spesa registrata nell\'esercizio)'
                    : stats.expenses_source === 'nessuna'
                      ? 'Nessuna spesa né addebito registrato'
                      : 'Spese e fatture registrate'}
                </p>
</CardContent>
            </Card>

            <Card className="border-stone-200 shadow-sm hover:shadow-md transition-all">
              <CardHeader className="flex flex-row items-center justify-between pb-2 px-3 sm:px-6">
                <CardTitle className="text-xs sm:text-sm font-medium text-stone-600">
                  Saldo
                </CardTitle>
                <Euro className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" strokeWidth={1.5} />
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                {/* Il saldo del conto, dove il dato c'è: è l'unico numero che
                    coincide con l'estratto conto. La differenza fra le due card
                    sopra, da quando le spese sono registrate, sottrae addebiti
                    documentali da accrediti bancari e non è una grandezza omogenea. */}
                <div className={`text-lg sm:text-3xl font-heading font-bold ${
                  (stats.bank_balance ?? stats.balance) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrency(stats.bank_balance ?? stats.balance)}
                </div>
                {stats.bank_balance != null ? (
                  <>
                    <p className="text-xs text-stone-600 mt-1 hidden sm:block">
                      Saldo del conto corrente
                      {stats.bank_balance_date
                        ? ` al ${new Date(stats.bank_balance_date).toLocaleDateString('it-IT')}`
                        : ''}
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5 hidden sm:block">
                      Movimenti dell'anno: {formatCurrency(stats.bank_year_net)}
                      {stats.income_source !== stats.expenses_source
                        ? ' · le card Entrate e Uscite hanno fonti diverse e non si sottraggono'
                        : ''}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-stone-600 mt-1 hidden sm:block">
                    Entrate − Uscite (saldo relativo: manca il saldo di apertura del conto)
                  </p>
                )}
</CardContent>
            </Card>
          </div>

          {/* Second Row - Aggregated Data */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <Card className="border-stone-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base sm:text-lg font-heading font-semibold">
                    Morosità Aggregata
                  </CardTitle>
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                </div>
                <CardDescription className="text-xs sm:text-sm">
                  Quote emesse e non incassate, esercizio {stats.period}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Prima leggeva le fatture fornitori in sospeso dell'anno e con
                    nessuna fattura datata nell'anno mostrava 0,00: ora sono le quote. */}
                <div className="flex items-center justify-between p-3 sm:p-4 bg-yellow-50 rounded-lg">
                  <div>
                    <p className="text-xs sm:text-sm text-muted-foreground">Scaduto a oggi</p>
                    <p className="text-xl sm:text-2xl font-bold text-yellow-700">
                      {formatCurrency(stats.total_unpaid)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs sm:text-sm text-muted-foreground">Posizioni</p>
                    <p className="text-xl sm:text-2xl font-bold text-yellow-700">
                      {stats.unpaid_count}
                    </p>
                  </div>
                </div>
                {stats.quotes_outstanding != null && (
                  <div className="mt-2 text-xs text-stone-600 space-y-0.5">
                    <div className="flex justify-between">
                      <span>Non ancora scaduto (mesi futuri)</span>
                      <span className="font-medium">{formatCurrency(stats.quotes_not_due)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Residuo totale dell'esercizio, {stats.quotes_outstanding_positions} posizioni</span>
                      <span className="font-medium">{formatCurrency(stats.quotes_outstanding)}</span>
                    </div>
                    <div className="flex justify-between border-t border-stone-200 pt-1 mt-1">
                      <span>Tutti gli esercizi: scaduto, {stats.quotes_overdue_positions_all_years} posizioni</span>
                      <span className="font-semibold text-yellow-800">{formatCurrency(stats.quotes_overdue_all_years)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tutti gli esercizi: residuo, {stats.quotes_positions_all_years} posizioni</span>
                      <span className="font-medium">{formatCurrency(stats.quotes_outstanding_all_years)}</span>
                    </div>
                  </div>
                )}
{/* Situazione al subentro: senza questa riga gli arretrati ereditati
                    dalla gestione precedente non comparirebbero da nessuna parte. */}
                {(stats.opening_credits > 0 || stats.opening_debts > 0) && (
                  <div className="mt-3 flex items-center justify-between p-3 rounded-lg bg-stone-50 border border-stone-200">
                    <div>
                      <p className="text-xs text-stone-600">
                        Crediti verso condòmini al subentro
                        {stats.opening_reference_date
                          ? ` (${new Date(stats.opening_reference_date).toLocaleDateString('it-IT')})`
                          : ''}
                      </p>
<p className="text-lg font-bold text-stone-800">
                        {formatCurrency(stats.opening_credits)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-stone-600">Debiti verso condòmini</p>
                      <p className="text-lg font-bold text-stone-800">
                        {formatCurrency(stats.opening_debts)}
                      </p>
                    </div>
                  </div>
                )}
                {stats.opening_entries_count > 0 && (
                  <p className="mt-1 text-xs text-stone-500">
                    Prospetto della precedente gestione, {stats.opening_entries_count} posizioni:
                    non si somma alle quote qui sopra.
                  </p>
                )}
</CardContent>
            </Card>

            {canSeeDetails ? (
              <Card className="border-stone-200 shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base sm:text-lg font-heading font-semibold">
                      Debiti Fornitori
                    </CardTitle>
                    <Badge className="bg-blue-100 text-blue-800 text-xs">Solo Admin</Badge>
                  </div>
                  <CardDescription className="text-xs sm:text-sm">
                    Fatture da pagare ai fornitori
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between p-3 sm:p-4 bg-blue-50 rounded-lg">
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Scaduto totale
                      </p>
                      <p className="text-xl sm:text-2xl font-bold text-blue-700">
                        {formatCurrency(
                          stats.supplier_debts_total ?? stats.supplier_debts
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs sm:text-sm text-muted-foreground">Fatture</p>
                      <p className="text-xl sm:text-2xl font-bold text-blue-700">
                        {(stats.pending_invoices_count || 0) +
                          (stats.pending_invoices_prior_years_count || 0)}
                      </p>
                    </div>
                  </div>
                  {/* In un subentro i debiti della gestione precedente portano una data
                      anteriore: mostrarli separati evita di far sembrare il condominio
                      senza debiti. */}
                  {stats.supplier_debts_prior_years > 0 && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <div>
                        <p className="text-xs text-amber-900">
                          di cui arretrati di gestioni precedenti
                        </p>
                        <p className="text-lg font-bold text-amber-800">
                          {formatCurrency(stats.supplier_debts_prior_years)}
                        </p>
                      </div>
                      <p className="text-xs text-amber-900">
                        {stats.pending_invoices_prior_years_count} partite
                      </p>
                    </div>
                  )}
                  {stats.supplier_debts_by_year && stats.supplier_debts_by_year.length > 0 && (
                    <div className="text-xs text-stone-600 space-y-0.5">
                      {stats.supplier_debts_by_year.map((r) => (
                        <div key={r.year} className="flex justify-between">
                          <span>Documenti datati {r.year}: {r.count} partite</span>
                          <span className="font-medium">{formatCurrency(r.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
</CardContent>
              </Card>
            ) : (
              <Card className="border-stone-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg font-heading font-semibold">
                    Comunicazioni
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Ultimi avvisi condominiali
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-3 sm:p-4 bg-stone-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Bell className="h-8 w-8 text-stone-400" />
                      <div>
                        <p className="text-sm font-medium">Bacheca Comunicazioni</p>
                        <p className="text-xs text-muted-foreground">Clicca per vedere gli avvisi</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => navigate('/communications')}>
                      Apri
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Charts Row */}
          {stats.income_trend && stats.expense_trend && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <Card className="border-stone-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg font-heading font-semibold">
                    Andamento Entrate {stats.period}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SimpleBarChart
                    data={stats.income_trend}
                    color="bg-green-500"
                    label={stats.income_source === 'banca' ? 'Accrediti bancari mensili' : 'Entrate mensili'}
                  />
</CardContent>
              </Card>

              <Card className="border-stone-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg font-heading font-semibold">
                    Andamento Uscite {stats.period}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SimpleBarChart
                    data={stats.expense_trend}
                    color="bg-red-500"
                    label={stats.expenses_source === 'banca' ? 'Addebiti bancari mensili' : 'Uscite mensili'}
                  />
                </CardContent>
              </Card>

              {stats.collections_trend && stats.collections_trend.length > 0 && (
                <Card className="border-stone-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base sm:text-lg font-heading font-semibold">
                      Incassi quote {stats.period}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      Per mese di pagamento, tutti gli esercizi: {formatCurrency(stats.collections_in_year)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SimpleBarChart
                      data={stats.collections_trend}
                      color="bg-teal-600"
                      label="Incassi confermati per mese"
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* All Alerts Section (if there are medium/low priority) */}
          {canSeeDetails && alerts.filter(a => a.priority === 'MEDIUM' || a.priority === 'LOW').length > 0 && (
            <Card className="border-stone-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base sm:text-lg font-heading font-semibold">
                    Altre Segnalazioni
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={fetchAlerts}
                    disabled={alertsLoading}
                  >
                    <RefreshCw className={`h-4 w-4 ${alertsLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {alerts.filter(a => a.priority === 'MEDIUM' || a.priority === 'LOW').slice(0, 9).map((alert) => (
                    <AlertCard key={alert.id} alert={alert} onClick={handleAlertClick} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* New Condominium Dialog */}
      <Dialog open={newCondoOpen} onOpenChange={setNewCondoOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Condominio</DialogTitle>
            <DialogDescription>
              Inserisci i dati del nuovo condominio. Diventerai automaticamente amministratore.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Nome Condominio *</Label>
              <Input
                id="name"
                value={newCondoForm.name}
                onChange={(e) => setNewCondoForm({...newCondoForm, name: e.target.value})}
                placeholder="Es. Condominio Via Roma 1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="address">Indirizzo</Label>
                <Input
                  id="address"
                  value={newCondoForm.address}
                  onChange={(e) => setNewCondoForm({...newCondoForm, address: e.target.value})}
                  placeholder="Via/Piazza..."
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="city">Città</Label>
                <Input
                  id="city"
                  value={newCondoForm.city}
                  onChange={(e) => setNewCondoForm({...newCondoForm, city: e.target.value})}
                  placeholder="Milano"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="postal_code">CAP</Label>
                <Input
                  id="postal_code"
                  value={newCondoForm.postal_code}
                  onChange={(e) => setNewCondoForm({...newCondoForm, postal_code: e.target.value})}
                  placeholder="20100"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="vat_number">Codice Fiscale</Label>
                <Input
                  id="vat_number"
                  value={newCondoForm.vat_number}
                  onChange={(e) => setNewCondoForm({...newCondoForm, vat_number: e.target.value})}
                  placeholder="12345678901"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="pec">PEC</Label>
                <Input
                  id="pec"
                  type="email"
                  value={newCondoForm.pec}
                  onChange={(e) => setNewCondoForm({...newCondoForm, pec: e.target.value})}
                  placeholder="condominio@pec.it"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="iban">IBAN</Label>
                <Input
                  id="iban"
                  value={newCondoForm.iban}
                  onChange={(e) => setNewCondoForm({...newCondoForm, iban: e.target.value})}
                  placeholder="IT..."
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCondoOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreateCondominium} disabled={creating}>
              {creating && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Crea Condominio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cruscotto: grafici per anno e criticita', ricalcolati a ogni apertura */}
      {condoId && (
        <div className="mt-8">
          <CruscottoSezione />
        </div>
      )}

      {/* Archive Condominium Dialog */}
      {condoId && (
        <ArchiveCondominiumDialog
          open={archiveDialogOpen}
          onOpenChange={setArchiveDialogOpen}
          condominiumId={condoId}
          condominiumName={condoName}
          onArchived={() => {
            toast.success('Condominio archiviato con successo');
            if (refreshCondominiums) {
              refreshCondominiums();
            }
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
